import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Reorder } from 'framer-motion';
import { MAX_STAGE_SCORE } from '@h2h/shared';
import { GripVertical, Lock, TrendingUp, Trophy } from '../../ui/icons';
import { useHaptics } from '../../../hooks/useHaptics';
import { cn } from '../../../lib/cn';
import { LockCountdownBadge } from './LockCountdownBadge';
import { LadderWeightPill } from './LadderWeightPill';
import type { LadderRungGrade } from './ladderTypes';
import { StakeLadderRung, type LadderRungData } from './StakeLadderRung';
import { StakeLadderRungReadOnly, LadderDuelDetailPanel } from './StakeLadderRungReadOnly';

export type { LadderRungData };
export { LockCountdownBadge };

/**
 * Drag-to-rank stake ladder — the hub "Duels of the day" card and Step 2 of the
 * picks flow share this component so confidence ranking looks identical everywhere.
 *
 * Performance: multipliers sit in a fixed left column (never re-render mid-drag);
 * rider rows reorder beside them. Parent state commits on drag end only so the
 * screen above doesn't thrash while your finger moves.
 */
export function StakeLadder({
  rungs,
  weights,
  disabled = false,
  onReorder,
  footerVariant = 'ranked',
  rankedCount,
  total,
  embedded = false,
  /** Static read-only ladder — Stages tab and duel detail (no drag, no Reorder). */
  readOnly = false,
  onRungPress,
  rungGrades,
  stageLive = false,
  highlightDuelId,
  showDetailPanel = false,
  footerHint
}: {
  rungs: LadderRungData[];
  weights: number[];
  disabled?: boolean;
  onReorder?: (next: LadderRungData[]) => void;
  footerVariant?: 'ranked' | 'reorder';
  rankedCount?: number;
  total?: number;
  embedded?: boolean;
  readOnly?: boolean;
  onRungPress?: (duelId: string) => void;
  rungGrades?: Map<string, LadderRungGrade>;
  stageLive?: boolean;
  highlightDuelId?: string;
  /** When set with highlightDuelId, duel detail renders below the ladder — not inside a row. */
  showDetailPanel?: boolean;
  footerHint?: string;
}) {
  const { impact } = useHaptics();
  const topWeight = Math.max(...weights, 0);

  // Derive visual state from existing props so callers don't need an extra prop.
  const canEdit = !disabled && !readOnly && !!onReorder;
  const isLocked = stageLive && (disabled || readOnly || !onReorder);
  const hasSettledGrades = !!rungGrades && [...rungGrades.values()].some((g) => g.status === 'settled');
  const isSettled = readOnly && hasSettledGrades;
  const ladderStatus: 'editable' | 'locked' | 'settled' | 'none' =
    canEdit ? 'editable' : isLocked ? 'locked' : isSettled ? 'settled' : 'none';

  const settledTotal = isSettled && rungGrades
    ? [...rungGrades.values()].reduce((s, g) => s + (g.points ?? 0), 0)
    : null;
  const propIds = useMemo(() => rungs.map((r) => r.duelId), [rungs]);
  const byId = useMemo(() => new Map(rungs.map((r) => [r.duelId, r])), [rungs]);

  const [localIds, setLocalIds] = useState(propIds);
  const draggingRef = useRef(false);
  const localIdsRef = useRef(localIds);
  localIdsRef.current = localIds;

  useEffect(() => {
    if (!draggingRef.current) setLocalIds(propIds);
  }, [propIds]);

  const onDragHaptic = useCallback(() => impact('selection'), [impact]);

  const commitOrder = useCallback(
    (nextIds: string[]) => {
      if (disabled || !onReorder) return;
      onReorder(nextIds.map((id) => byId.get(id)!).filter(Boolean));
    },
    [disabled, onReorder, byId]
  );

  const handleReorder = useCallback(
    (nextIds: string[]) => {
      if (disabled || !onReorder) return;
      setLocalIds(nextIds);
    },
    [disabled, onReorder]
  );

  const handleDragStart = useCallback(() => {
    draggingRef.current = true;
    onDragHaptic();
  }, [onDragHaptic]);

  const handleDragEnd = useCallback(() => {
    draggingRef.current = false;
    commitOrder(localIdsRef.current);
  }, [commitOrder]);

  const move = useCallback(
    (from: number, to: number) => {
      if (disabled || !onReorder) return;
      setLocalIds((cur) => {
        if (to < 0 || to >= cur.length || from === to) return cur;
        onDragHaptic();
        const next = [...cur];
        const [row] = next.splice(from, 1);
        next.splice(to, 0, row);
        commitOrder(next);
        return next;
      });
    },
    [disabled, onReorder, onDragHaptic, commitOrder]
  );

  const highlightIdx = highlightDuelId ? rungs.findIndex((r) => r.duelId === highlightDuelId) : -1;
  const detailRung = highlightDuelId ? rungs.find((r) => r.duelId === highlightDuelId) : undefined;
  const detailGrade = highlightDuelId ? rungGrades?.get(highlightDuelId) : undefined;
  const showDetail = showDetailPanel && !!detailRung && !!detailGrade;

  return (
    <div
      className={cn(
        !embedded &&
          'overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]'
      )}
    >
      <div className="relative px-2.5 pb-1.5 pt-3.5">
        {/* ── State banner ───────────────────────────────────────────────────── */}
        {ladderStatus !== 'none' && (
          <div
            className={cn(
              'mb-3 flex items-center gap-[7px] rounded-[10px] px-2.5 py-[7px]',
              ladderStatus === 'editable' && 'bg-accent/[0.07]',
              ladderStatus === 'locked' && 'bg-[rgba(239,65,53,0.07)]',
              ladderStatus === 'settled' && 'bg-[#F4F4F7]'
            )}
          >
            {ladderStatus === 'editable' && (
              <GripVertical width={12} height={12} strokeWidth={2.2} className="shrink-0 text-accent-ink" />
            )}
            {ladderStatus === 'locked' && (
              <span className="h-[6px] w-[6px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
            )}
            {ladderStatus === 'settled' && (
              <Trophy width={12} height={12} strokeWidth={2.2} className="shrink-0 text-ink3" />
            )}
            <span
              className={cn(
                'flex-1 text-[11.5px] font-semibold leading-none',
                ladderStatus === 'editable' && 'text-accent-ink',
                ladderStatus === 'locked' && 'text-[#C0392B]',
                ladderStatus === 'settled' && 'text-ink3'
              )}
            >
              {ladderStatus === 'editable' && 'Drag to rank · saves as you go'}
              {ladderStatus === 'locked' && 'Stage rolling · locked in'}
              {ladderStatus === 'settled' && 'Stage settled'}
              {ladderStatus === 'settled' && settledTotal != null && (
                <>
                  {' '}·{' '}
                  <span className={cn('font-extrabold', settledTotal >= 0 ? 'text-accent-ink' : 'text-miss')}>
                    {settledTotal > 0 ? '+' : ''}{settledTotal} pts
                  </span>
                </>
              )}
            </span>
            {ladderStatus === 'locked' && (
              <Lock width={11} height={11} strokeWidth={2.2} className="shrink-0 text-[#C0392B]" />
            )}
          </div>
        )}

        <div className="relative flex gap-[11px]">
          {/* Pill column — line lives here so left-1/2 always hits the circle centre. */}
          <div className="relative z-[1] shrink-0 pl-0">
            <div
              className={cn(
                'pointer-events-none absolute left-1/2 top-8 bottom-8 w-0.5 -translate-x-1/2 rounded-sm bg-gradient-to-b',
                // z-[-1] keeps the line behind the pill circles (positioned elements paint
                // above non-positioned flow content by default in the same stacking context).
                'z-[-1]',
                ladderStatus === 'locked' ? 'from-[#EF4135] to-[#E6E6EC]' :
                ladderStatus === 'settled' ? 'from-[#9898A0] to-[#E6E6EC]' :
                'from-accent to-[#E6E6EC]'
              )}
              aria-hidden
            />
            {weights.map((weight, i) => (
              <LadderWeightPill key={i} weight={weight} topWeight={topWeight} highlighted={i === highlightIdx} />
            ))}
          </div>

          {readOnly ? (
            <div className="relative m-0 min-w-0 flex-1 p-0">
              {rungs.map((rung) => (
                <StakeLadderRungReadOnly
                  key={rung.duelId}
                  data={rung}
                  grade={rungGrades?.get(rung.duelId)}
                  stageLive={stageLive}
                  highlighted={rung.duelId === highlightDuelId}
                  onPress={onRungPress ? () => onRungPress(rung.duelId) : undefined}
                />
              ))}
            </div>
          ) : (
          <Reorder.Group
            axis="y"
            values={localIds}
            onReorder={handleReorder}
            className="relative m-0 min-w-0 flex-1 list-none p-0"
          >
            {localIds.map((id, i) => {
              const data = byId.get(id);
              if (!data) return null;
              return (
                <StakeLadderRung
                  key={id}
                  id={id}
                  data={data}
                  rank={i}
                  total={localIds.length}
                  disabled={disabled || !onReorder}
                  onMove={move}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              );
            })}
          </Reorder.Group>
          )}
        </div>
      </div>

      {showDetail && (
        <LadderDuelDetailPanel data={detailRung} grade={detailGrade} stageLive={stageLive} />
      )}

      {ladderStatus === 'locked' ? (
        <div className="flex items-center gap-2.5 border-t border-[rgba(239,65,53,0.15)] bg-[rgba(239,65,53,0.05)] px-[18px] py-[13px]">
          <Lock width={15} height={15} strokeWidth={2.2} className="shrink-0 text-[#C0392B]" />
          <span className="flex-1 text-[12.5px] font-semibold tracking-[-0.01em] text-[#C0392B]">
            Ladder locked · <span className="font-extrabold">waiting for results</span>
          </span>
          <span className="h-[6px] w-[6px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
        </div>
      ) : ladderStatus === 'settled' ? (
        <div className="flex items-center gap-2.5 border-t border-black/[0.06] bg-[#FBFBFD] px-[18px] py-[13px]">
          <Trophy width={15} height={15} strokeWidth={2.2} className="shrink-0 text-ink3" />
          <span className="flex-1 text-[12.5px] font-semibold tracking-[-0.01em] text-ink3">
            {footerHint ?? 'Stage settled'}
          </span>
          {settledTotal != null && (
            <span className={cn('num text-[15px] font-extrabold', settledTotal >= 0 ? 'text-accent-ink' : 'text-miss')}>
              {settledTotal > 0 ? '+' : ''}{settledTotal} pts
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2.5 border-t border-black/[0.06] bg-[#FBFBFD] px-[18px] py-[13px]">
          <TrendingUp width={16} height={16} strokeWidth={2.2} className="shrink-0 text-accent-ink" />
          <span className="flex-1 text-[12.5px] font-semibold tracking-[-0.01em] text-ink2">
            {footerHint ?? (footerVariant === 'ranked' ? (
              <>
                All six ranked · up to <span className="font-extrabold text-ink">{MAX_STAGE_SCORE} pts</span> on the line
              </>
            ) : (
              <>
                Up to <span className="font-extrabold text-ink">{MAX_STAGE_SCORE} pts</span> on the line
              </>
            ))}
          </span>
          {footerHint ? null : footerVariant === 'ranked' && rankedCount != null && total != null ? (
            <span className="num text-xs font-extrabold text-accent-ink">
              {rankedCount} / {total}
            </span>
          ) : (
            <span className="text-[11px] font-bold text-ink3">Drag to reorder</span>
          )}
        </div>
      )}
    </div>
  );
}

/** Compact section head above the ladder card on hub / rank step. */
export function StakeLadderHead({
  eyebrow,
  title,
  body,
  lockAt,
  now,
  badge
}: {
  eyebrow: string;
  title: string;
  body: React.ReactNode;
  lockAt?: number | null;
  now?: number;
  badge?: React.ReactNode;
}) {
  return (
    <div className="px-[18px] pb-0 pt-5">
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.09em] text-ink3">{eyebrow}</div>
          <div className="mt-[3px] text-[21px] font-extrabold leading-[1.04] tracking-[-0.028em] text-ink">{title}</div>
        </div>
        {badge ?? (lockAt != null && now != null && lockAt > now ? (
          <LockCountdownBadge lockAt={lockAt} now={now} />
        ) : null)}
      </div>
      <p className="mt-[9px] text-[12.5px] font-medium leading-[1.45] text-ink3">{body}</p>
    </div>
  );
}
